import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { 
  ArrowRight, 
  ArrowLeft,
  Loader2, 
  Building2, 
  Globe, 
  Users, 
  Target, 
  Crosshair, 
  MapPin,
  User,
  Mail,
  Phone,
  CheckCircle2,
  Shield,
  Lock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandInputForm = ({ onReportCreated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // Step 1: Contact Info, Step 2: Business Info
  
  const [formData, setFormData] = useState({
    // Contact Info (Step 1)
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    // Business Info (Step 2)
    company_name: "",
    website_url: "",
    industry: "",
    target_audience: "",
    competitors: "",
    goals: "",
    location: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.contact_name.trim()) newErrors.contact_name = "Name is required";
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = "Please enter a valid email";
    }
    if (!formData.contact_phone.trim()) newErrors.contact_phone = "Phone is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) newErrors.company_name = "Company name is required";
    if (!formData.industry.trim()) newErrors.industry = "Industry is required";
    if (!formData.target_audience.trim()) newErrors.target_audience = "Target audience is required";
    if (!formData.competitors.trim()) newErrors.competitors = "Competitors are required";
    if (!formData.goals.trim()) newErrors.goals = "Goals are required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handlePrevStep = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API}/reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to create report");
      }

      const data = await response.json();
      onReportCreated(data.id);
    } catch (error) {
      setErrors({ submit: "Failed to create report. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const contactFields = [
    {
      name: "contact_name",
      label: "Your Name",
      placeholder: "John Smith",
      icon: User,
      type: "input",
    },
    {
      name: "contact_email",
      label: "Email Address",
      placeholder: "john@company.com",
      icon: Mail,
      type: "input",
      inputType: "email",
    },
    {
      name: "contact_phone",
      label: "Phone Number",
      placeholder: "(555) 123-4567",
      icon: Phone,
      type: "input",
      inputType: "tel",
    },
  ];

  const businessFields = [
    {
      name: "company_name",
      label: "Company Name",
      placeholder: "e.g., Smith's Auto Repair",
      icon: Building2,
      type: "input",
    },
    {
      name: "website_url",
      label: "Website URL (Optional)",
      placeholder: "https://yourcompany.com",
      icon: Globe,
      type: "input",
    },
    {
      name: "industry",
      label: "Industry",
      placeholder: "e.g., Auto Repair, Restaurant, Fitness",
      icon: Target,
      type: "input",
    },
    {
      name: "location",
      label: "Location (for Google Reviews)",
      placeholder: "e.g., Austin, TX or Chicago, IL",
      icon: MapPin,
      type: "input",
    },
    {
      name: "target_audience",
      label: "Target Audience",
      placeholder: "Describe your ideal customers (age, interests, pain points)",
      icon: Users,
      type: "textarea",
    },
    {
      name: "competitors",
      label: "Main Competitors (Add up to 3 businesses by name)",
      placeholder: "List competitors separated by commas (e.g., Joe's Auto, Quick Fix Garage)",
      icon: Crosshair,
      type: "textarea",
    },
    {
      name: "goals",
      label: "Business Goals",
      placeholder: "What do you want to achieve? (e.g., more customers, better reviews, stand out from competition)",
      icon: Target,
      type: "textarea",
    },
  ];

  const renderField = (field) => {
    const IconComponent = field.icon;
    return (
      <div key={field.name}>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
          <IconComponent className="w-4 h-4 text-emerald-400" />
          {field.label}
        </label>
        {field.type === "input" ? (
          <Input
            name={field.name}
            type={field.inputType || "text"}
            value={formData[field.name]}
            onChange={handleChange}
            placeholder={field.placeholder}
            data-testid={`input-${field.name}`}
            className={`h-12 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20 rounded-xl ${
              errors[field.name] ? "border-red-500" : ""
            }`}
          />
        ) : (
          <Textarea
            name={field.name}
            value={formData[field.name]}
            onChange={handleChange}
            placeholder={field.placeholder}
            rows={3}
            data-testid={`textarea-${field.name}`}
            className={`bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20 rounded-xl resize-none ${
              errors[field.name] ? "border-red-500" : ""
            }`}
          />
        )}
        {errors[field.name] && (
          <p className="text-red-400 text-sm mt-1">{errors[field.name]}</p>
        )}
      </div>
    );
  };

  return (
    <section className="py-16 px-4 bg-slate-950 min-h-screen">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Create Your Brand Strategy
          </h2>
          <p className="text-slate-400">
            {step === 1 
              ? "First, tell us how to reach you so we can send your report"
              : "Now, tell us about your business"
            }
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-emerald-400' : 'text-slate-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 1 ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-400'
            }`}>
              {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
            </div>
            <span className="text-sm font-medium hidden sm:inline">Contact Info</span>
          </div>
          <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-emerald-500' : 'bg-slate-700'}`} />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-emerald-400' : 'text-slate-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 2 ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-400'
            }`}>
              2
            </div>
            <span className="text-sm font-medium hidden sm:inline">Business Details</span>
          </div>
        </div>

        <form
          onSubmit={step === 1 ? handleNextStep : handleSubmit}
          className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 md:p-10"
        >
          {step === 1 ? (
            <>
              {/* Step 1: Contact Information */}
              <div className="space-y-6">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 mb-6">
                  <p className="text-emerald-400 text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Your brand strategy report will be emailed to you once it's ready!
                  </p>
                </div>
                {contactFields.map(renderField)}
                
                {/* Security Badge */}
                <div className="flex items-center justify-center gap-4 pt-4 border-t border-slate-800">
                  <div className="flex items-center gap-2 text-slate-500 text-xs">
                    <Lock className="w-3.5 h-3.5 text-emerald-500" />
                    <span>256-bit SSL Encrypted</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500 text-xs">
                    <Shield className="w-3.5 h-3.5 text-emerald-500" />
                    <span>Privacy Protected</span>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="next-step-btn"
                className="w-full mt-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-emerald-500/25 transition-all"
              >
                Continue to Business Details
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              
              {/* Terms agreement note */}
              <p className="text-center text-slate-600 text-xs mt-4">
                By continuing, you agree to our{" "}
                <Link to="/terms" className="text-emerald-500 hover:underline">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" className="text-emerald-500 hover:underline">Privacy Policy</Link>
              </p>
            </>
          ) : (
            <>
              {/* Step 2: Business Information */}
              <div className="space-y-6">
                {businessFields.map(renderField)}
              </div>

              {errors.submit && (
                <p className="text-red-400 text-sm mt-4 text-center">{errors.submit}</p>
              )}

              <div className="flex gap-4 mt-8">
                <Button
                  type="button"
                  onClick={handlePrevStep}
                  variant="outline"
                  data-testid="prev-step-btn"
                  className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-full px-8 py-6 text-lg font-medium"
                >
                  <ArrowLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  data-testid="submit-report-btn"
                  className="flex-[2] bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-emerald-500/25 transition-all disabled:opacity-70"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      Generate Brand Strategy
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>

              <p className="text-center text-slate-500 text-sm mt-4">
                Takes about 1-2 minutes • Report will be emailed to {formData.contact_email}
              </p>
            </>
          )}
        </form>
      </div>
    </section>
  );
};

export default BrandInputForm;
